// The last Launch workflow (Create, Edit or Budgets) and platform (Meta or TikTok) an
// operator used in a workspace, kept in browser storage so the sidebar link,
// the command palette and a bare /launch URL reopen where they left off. The
// URL stays authoritative: a link with ?platform= always wins, and nothing
// deeper (a campaign, an ad set) is remembered here because the Edit ladder
// already lives in the URL.

export type LaunchWorkflow = "create" | "edit" | "budgets";
export type LaunchPlatform = "meta" | "tiktok";

export interface LaunchPlace {
  workflow: LaunchWorkflow;
  platform: LaunchPlatform;
}

type PlaceStorage = Pick<Storage, "getItem" | "setItem">;
const storageKey = (orgSlug: string) => `toolkit:launch-place:v1:${orgSlug}`;

const WORKFLOWS = new Set<string>(["create", "edit", "budgets"]);
const PLATFORMS = new Set<string>(["meta", "tiktok"]);

const browserStorage = (): PlaceStorage | undefined =>
  typeof window === "undefined" ? undefined : window.localStorage;

export function readLaunchPlace(orgSlug: string, storage = browserStorage()): Partial<LaunchPlace> {
  try {
    const raw: unknown = JSON.parse(storage?.getItem(storageKey(orgSlug)) ?? "null");
    if (!raw || typeof raw !== "object" || !("version" in raw) || raw.version !== 1) return {};
    const place: Partial<LaunchPlace> = {};
    if ("workflow" in raw && typeof raw.workflow === "string" && WORKFLOWS.has(raw.workflow)) {
      place.workflow = raw.workflow as LaunchWorkflow;
    }
    if ("platform" in raw && typeof raw.platform === "string" && PLATFORMS.has(raw.platform)) {
      place.platform = raw.platform as LaunchPlatform;
    }
    return place;
  } catch {
    return {};
  }
}

export function saveLaunchPlace(
  orgSlug: string,
  place: LaunchPlace,
  storage = browserStorage(),
): void {
  try {
    storage?.setItem(
      storageKey(orgSlug),
      JSON.stringify({ version: 1, workflow: place.workflow, platform: place.platform }),
    );
  } catch {
    // Private browsing or a full device never blocks Launch itself.
  }
}

/** Where a Launch link should land: the remembered place, else Create on Meta. */
export function resolveLaunchPlace(remembered: Partial<LaunchPlace>): LaunchPlace {
  return {
    workflow: remembered.workflow ?? "create",
    platform: remembered.platform ?? "meta",
  };
}

const workflowPath = (workflow: LaunchWorkflow) =>
  workflow === "edit"
    ? ("/$orgSlug/launch/edit" as const)
    : workflow === "budgets"
      ? ("/$orgSlug/launch/budgets" as const)
      : ("/$orgSlug/launch/create" as const);

/**
 * The router target for a workspace's Launch link: its remembered workflow and
 * platform. Nothing remembered means Create with no search, so the link stays
 * the plain one a fresh workspace shows.
 */
export function launchLinkFor(orgSlug: string, storage = browserStorage()) {
  const remembered = readLaunchPlace(orgSlug, storage);
  const place = resolveLaunchPlace(remembered);
  return {
    to: workflowPath(place.workflow),
    params: { orgSlug },
    ...(remembered.platform ? { search: { platform: remembered.platform } } : {}),
  };
}

/**
 * For a Launch route opened without a platform in its URL: the redirect that
 * reopens the remembered platform, or null when Meta (the default) is fine.
 * `search` is absent when the route's beforeLoad is called outside the router.
 */
export function rememberedPlatformRedirect(
  workflow: LaunchWorkflow,
  orgSlug: string,
  search: { platform?: LaunchPlatform } | undefined,
  storage = browserStorage(),
) {
  if (search?.platform) return null;
  const remembered = readLaunchPlace(orgSlug, storage).platform;
  if (!remembered || remembered === "meta") return null;
  return {
    to: workflowPath(workflow),
    params: { orgSlug },
    search: { platform: remembered },
    replace: true as const,
  };
}
